import { AuthApi } from './endpoints/auth';
import { ChatsApi } from './endpoints/chats';
import { MediaApi } from './endpoints/media';
import { MessagesApi } from './endpoints/messages';
import {
  CallsApi,
  ConfigApi,
  DevicesApi,
  NotificationsApi,
  ProfileApi,
  SearchApi,
  SyncApi,
} from './endpoints/misc';
import { LiniyaHttpClient, type HttpClientOptions } from './http/client';

export * from './http/client';
export * from './http/errors';
export * from './http/token-store';
export * from './base64';
export * from './digest';
export * from './platform';
export * from './message-meta';
export * from './crypto/encryption-service';
export * from './websocket/client';
export * from './websocket/events';
export * from './websocket/reconnect';
export * from './endpoints/auth';
export * from './endpoints/chats';
export * from './endpoints/media';
export * from './endpoints/messages';
export * from './endpoints/misc';
export type * from './types/api';
export type * from './types/attachment';
export type * from './types/auth';
export type * from './types/call';
export type * from './types/chat';
export type * from './types/config';
export type * from './types/device';
export type * from './types/message';
export type * from './types/user';
export type * from './types/websocket';
export { PROTOCOL_VERSION } from './types/websocket';

/**
 * Everything the API offers, assembled once.
 *
 * The whole surface is React-free on purpose: the web client wraps these calls
 * in TanStack Query hooks, and a React Native client will wrap the same calls
 * in whatever it uses, without either one reimplementing authentication,
 * refresh, retries or the WebSocket protocol.
 */
export class LiniyaApi {
  readonly http: LiniyaHttpClient;
  readonly auth: AuthApi;
  readonly chats: ChatsApi;
  readonly messages: MessagesApi;
  readonly media: MediaApi;
  readonly profile: ProfileApi;
  readonly devices: DevicesApi;
  readonly search: SearchApi;
  readonly calls: CallsApi;
  readonly notifications: NotificationsApi;
  readonly sync: SyncApi;
  readonly config: ConfigApi;

  constructor(options: HttpClientOptions = {}) {
    this.http = new LiniyaHttpClient(options);
    this.auth = new AuthApi(this.http);
    this.chats = new ChatsApi(this.http);
    this.messages = new MessagesApi(this.http);
    this.media = new MediaApi(this.http, options.baseUrl ?? '');
    this.profile = new ProfileApi(this.http);
    this.devices = new DevicesApi(this.http);
    this.search = new SearchApi(this.http);
    this.calls = new CallsApi(this.http);
    this.notifications = new NotificationsApi(this.http);
    this.sync = new SyncApi(this.http);
    this.config = new ConfigApi(this.http);
  }
}

export function createLiniyaApi(options: HttpClientOptions = {}): LiniyaApi {
  return new LiniyaApi(options);
}
